import { ArrowDown, ArrowUp, Milestone, MoreHorizontal, Plus, Settings2, Trash2 } from "lucide-react";
import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { CopyButton } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { Page, PageHeader, Toolbar } from "@/components/layout/page";
import { StageFormDialog } from "@/features/stage-form";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { catalogTenant } from "@/lib/dispositions";
import { useDeleteStage, useReorderStages, useStages, useTenants } from "@/lib/queries";
import {
  STAGE_GROUPS, exitScopeText, groupText, movedWithinGroup, removedSummary, replacementsFor,
  type StageGroupName,
} from "@/lib/stages";
import { atLeast, type StageRow } from "@/lib/types";

/** The groups' colours, as `stageText` gives every stage badge in the console. */
const GROUP_TONE = { working: "accent", scheduled: "warning", exit: "neutral" } as const;

/**
 * The tenant's stages (ADR-077): the lifecycle every contact moves through, in three groups.
 * Working stages are dialed, scheduled ones wait for their booking, and an exit leaves every dial
 * list. Outcomes put leads in a stage (the Dispositions page), the Contacts page counts them, a
 * line's caller rules route an inbound caller by them, and a voicemail message can be for one.
 *
 * Supervisors read it; admins add, edit, reorder and delete. The platform's four (New, Do not
 * call, Signed, Signed pending intake) are renamed and reordered but never deleted. Deleting any
 * other stage asks where whatever uses it goes — its leads, and the outcomes, caller rules and
 * voicemail messages that name it — and moves all of it in one step: an exit into another exit,
 * anything else into a stage that is not one, so a delete never puts a lead on a dial list or
 * takes one off. Reordering is within a group and saves at once. A superadmin names the tenant
 * (`?tenant=`), as on the other catalogs.
 */
export function StagesPage() {
  const { user: actor } = useAuth();
  const toast = useToast();
  const canManage = atLeast(actor?.role, "admin");
  const isSuperadmin = actor?.role === "superadmin";
  const [params, setParams] = useSearchParams();
  const tenants = useTenants({ enabled: isSuperadmin });
  const shownTenant = catalogTenant(actor, tenants.data, Number(params.get("tenant")) || null);
  // Everyone else reads their own tenant's, under the key the app shell's labels read.
  const tenantId = isSuperadmin ? shownTenant : null;
  const catalog = useStages(tenantId, {
    enabled: !isSuperadmin || shownTenant !== null,
    refetchOnMount: "always",
  });
  const reorder = useReorderStages();

  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<StageRow | undefined>();
  const [deleting, setDeleting] = React.useState<StageRow | undefined>();

  const rows = catalog.data ?? [];
  const openForm = (s?: StageRow) => {
    setEditing(s);
    setFormOpen(true);
  };

  const move = async (slug: string, by: -1 | 1) => {
    const slugs = movedWithinGroup(rows, slug, by);
    if (!slugs) return;
    try {
      await reorder.mutateAsync({ slugs, tenantId });
    } catch (e) {
      toast.error("Could not save the order", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  const newButton = canManage && (!isSuperadmin || shownTenant !== null) ? (
    <Button variant="default" onClick={() => openForm()}>
      <Plus />
      New stage
    </Button>
  ) : undefined;

  return (
    <Page>
      <PageHeader
        title="Stages"
        description="The lifecycle every contact moves through. An outcome puts a lead in a stage, the Contacts page counts them, a line's caller rules can route an inbound caller by theirs, and a voicemail message can be for one."
        action={newButton}
      />

      <Card className="mb-4">
        <dl className="grid gap-x-6 gap-y-3 px-4 py-3 sm:grid-cols-3">
          {STAGE_GROUPS.map((group) => (
            <div key={group} className="space-y-1">
              <dt>
                <Badge tone={GROUP_TONE[group]} size="sm">
                  {groupText(group).label}
                </Badge>
              </dt>
              <dd className="text-xs text-text-muted">{groupText(group).description}</dd>
            </div>
          ))}
        </dl>
        <p className="border-t border-border px-4 py-2 text-xs text-text-muted">
          <Badge tone="outline" size="sm" className="mr-1.5">
            Built in
          </Badge>
          New, Do not call, Signed and Signed pending intake are the platform's own: they can be renamed
          and reordered, not deleted. A stage's order is the order the pickers list it in, and for a
          lead with several records, the further along a stage is, the more it counts.
        </p>
      </Card>

      {isSuperadmin && (tenants.data?.length ?? 0) > 1 && (
        <Card className="mb-4 overflow-hidden">
          <Toolbar className="border-b-0">
            <Select
              value={shownTenant?.toString() ?? ""}
              onValueChange={(v) => setParams({ tenant: v }, { replace: true })}
            >
              <SelectTrigger className="w-56" aria-label="Tenant">
                <SelectValue placeholder="Choose a tenant" />
              </SelectTrigger>
              <SelectContent>
                {(tenants.data ?? []).map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Toolbar>
        </Card>
      )}

      {(isSuperadmin && tenants.isPending) || catalog.isPending ? (
        <Card className="overflow-hidden">
          <TableSkeleton cols={5} />
        </Card>
      ) : tenants.error ? (
        <Card className="overflow-hidden">
          <ErrorState
            error={tenants.error}
            title="Could not load the tenants"
            action={
              <Button variant="outline" onClick={() => void tenants.refetch()}>
                Try again
              </Button>
            }
          />
        </Card>
      ) : isSuperadmin && shownTenant === null ? (
        <Card className="overflow-hidden">
          <EmptyState
            icon={Milestone}
            title="No tenant to show"
            description="Stages belong to a tenant, and there is none to read them from."
          />
        </Card>
      ) : catalog.error ? (
        <Card className="overflow-hidden">
          <ErrorState
            error={catalog.error}
            title="Could not load the stages"
            action={
              <Button variant="outline" onClick={() => void catalog.refetch()}>
                Try again
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {STAGE_GROUPS.map((group) => (
            <GroupTable
              key={group}
              group={group}
              rows={rows.filter((s) => s.group === group)}
              canManage={canManage}
              moving={reorder.isPending}
              onMove={(slug, by) => void move(slug, by)}
              onEdit={openForm}
              onDelete={setDeleting}
            />
          ))}
        </div>
      )}

      <StageFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        stage={editing}
        catalog={rows}
        tenantId={tenantId}
      />

      <DeleteStageDialog
        stage={deleting}
        catalog={rows}
        tenantId={tenantId}
        onOpenChange={(open) => !open && setDeleting(undefined)}
      />
    </Page>
  );
}

function GroupTable({
  group, rows, canManage, moving, onMove, onEdit, onDelete,
}: {
  group: StageGroupName;
  rows: StageRow[];
  canManage: boolean;
  moving: boolean;
  onMove: (slug: string, by: -1 | 1) => void;
  onEdit: (stage: StageRow) => void;
  onDelete: (stage: StageRow) => void;
}) {
  const heading = groupText(group);
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-border px-4 py-2.5">
        <h2 className="text-sm font-semibold text-text">{heading.label}</h2>
        <p className="text-xs text-text-muted">{heading.description}</p>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-4 text-center text-xs text-text-muted">No {heading.label.toLowerCase()} stages.</p>
      ) : (
        // Fixed columns, so the three groups' tables line up under one another. They come to
        // 38.5rem; below the minimum width the table scrolls rather than squeeze "What it means".
        <Table className="min-w-[52.5rem] table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">#</TableHead>
              <TableHead className="w-56">Stage</TableHead>
              <TableHead>What it means</TableHead>
              <TableHead className="w-44">{group === "exit" ? "Who leaves with it" : "Dialing"}</TableHead>
              <TableHead className="w-16 text-right">Leads</TableHead>
              {canManage && <TableHead className="w-28" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((s, i) => {
              const reach = exitScopeText(s.exit_scope);
              return (
                <TableRow key={s.slug} className="[&>td]:h-11">
                  <TableCell className="text-xs tabular-nums text-text-subtle">{i + 1}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      <Badge tone={s.slug === "dnc" ? "danger" : GROUP_TONE[s.group]} size="sm">
                        {s.label}
                      </Badge>
                      {s.system && (
                        <Badge tone="outline" size="sm" title="One of the platform's four: renamed and reordered, never deleted">
                          Built in
                        </Badge>
                      )}
                    </span>
                    <span className="flex items-center gap-1 font-mono text-xs text-text-muted">
                      {s.slug}
                      <CopyButton value={s.slug} label="Copy the stored name" />
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-text-muted">
                    {s.description || <span className="text-text-subtle">—</span>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {group === "exit" ? (
                      <span title={reach?.description}>{reach?.label ?? "—"}</span>
                    ) : group === "scheduled" ? (
                      <span className="text-text-muted">After the booking</span>
                    ) : s.dial_boost ? (
                      <Badge tone="accent" size="sm" title="Ranked in the cadence's near-signing band">
                        Near the top
                      </Badge>
                    ) : (
                      <span className="text-text-muted">By the cadence</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {s.leads > 0 ? (
                      <Link
                        to={`/contacts?stage=${encodeURIComponent(s.slug)}`}
                        className="text-text hover:underline"
                        title="Open these contacts"
                      >
                        {s.leads.toLocaleString()}
                      </Link>
                    ) : (
                      <span className="text-text-subtle">0</span>
                    )}
                  </TableCell>
                  {canManage && (
                    <TableCell>
                      <span className="flex items-center justify-end gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Move ${s.label} up`}
                          disabled={i === 0 || moving}
                          onClick={() => onMove(s.slug, -1)}
                        >
                          <ArrowUp />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Move ${s.label} down`}
                          disabled={i === rows.length - 1 || moving}
                          onClick={() => onMove(s.slug, 1)}
                        >
                          <ArrowDown />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${s.label}`}>
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onSelect={() => onEdit(s)}>
                              <Settings2 />
                              Edit stage
                            </DropdownMenuItem>
                            {!s.system && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem destructive onSelect={() => onDelete(s)}>
                                  <Trash2 />
                                  Delete…
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </span>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

/**
 * Deleting a stage: always with a stage to move whatever uses it to. The list only knows the lead
 * count, and the outcomes, caller rules and voicemail messages that name the stage are core-api's to
 * count, so the dialog asks every time and the toast says what moved.
 */
function DeleteStageDialog({
  stage, catalog, tenantId, onOpenChange,
}: {
  stage: StageRow | undefined;
  catalog: readonly StageRow[];
  tenantId: number | null;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const remove = useDeleteStage();
  const options = React.useMemo(() => (stage ? replacementsFor(catalog, stage.slug) : []), [catalog, stage]);
  const [into, setInto] = React.useState("");
  const [problem, setProblem] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!stage) return;
    // The stage just before it in its group, else the first that may take its leads.
    const same = catalog.filter((s) => s.group === stage.group);
    const before = same[same.findIndex((s) => s.slug === stage.slug) - 1];
    setInto(before?.slug ?? options[0]?.slug ?? "");
    setProblem(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage?.slug]);

  if (!stage) return null;
  const exit = stage.group === "exit";
  const target = catalog.find((s) => s.slug === into);

  const confirm = () => {
    if (!into) {
      setProblem("Pick where its leads go.");
      return;
    }
    remove.mutate(
      { slug: stage.slug, replaceWith: into, tenantId },
      {
        onSuccess: (result) => {
          onOpenChange(false);
          toast.success(`${stage.label} deleted`, removedSummary(result, target?.label ?? null));
        },
        onError: (e) => setProblem(e instanceof ApiError ? e.message : "Could not delete the stage. Please try again."),
      },
    );
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Delete {stage.label}?</DialogTitle>
          <DialogDescription>
            {stage.leads > 0
              ? `Its ${stage.leads.toLocaleString()} lead${stage.leads === 1 ? "" : "s"} move to the stage you pick, with a line in each one's history.`
              : "No lead is in it now."}{" "}
            Any outcome, line caller rule or voicemail message that names it will name that stage instead;
            a line that already has a caller rule for that stage keeps its own.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field
            label="Move to"
            htmlFor="stage-replacement"
            required
            error={problem ?? undefined}
            hint={
              exit
                ? "Another exit: the leads stay off every dial list."
                : "A working or scheduled stage: the leads stay on the dial lists they are on."
            }
          >
            <Select value={into} onValueChange={(v) => { setInto(v); setProblem(null); }}>
              <SelectTrigger id="stage-replacement" aria-label="Move to">
                <SelectValue placeholder="Pick a stage" />
              </SelectTrigger>
              <SelectContent>
                {options.map((s) => (
                  <SelectItem key={s.slug} value={s.slug} description={s.description ?? undefined}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Callout tone="warning">
            Past history lines keep its stored name, <span className="font-mono">{stage.slug}</span>. A
            deleted stage cannot be brought back: add a new one if you need it again.
          </Callout>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="danger" loading={remove.isPending} onClick={confirm}>
            Delete stage
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
